import { useFormCsi } from 'app/providers/csi/hooks/useFormCsi';
import { useCustomNavigate } from 'app/utils/customHooks/useCustomNavigate';
import { useSendSalaryToCsi } from 'app/utils/services/salaryServices';
import { useLocation } from 'react-router-dom';

/** Endpoints (4th segment of path_url) whose success modal buttons go through form-CSI */
export const FORM_CSI_PATHS: string[] = ['transfer-page', 'payment_salary-make_payment-confirm'];

export type FormCsiAction = (btnType: string, response?: { business_key?: string }) => void;

/**
 * Success modal button handler for form-CSI: sends the button's form_id to the signing endpoint
 * and opens CsiModal when the backend answers with form_id === 'csi'.
 * Returns null when the endpoint does not use form-CSI.
 *
 * Moved as-is from AsanImza. Known legacy quirks are intentionally preserved
 * ('go-home' vs 'go_home', csiRedirectPage overwritten by the stale spread in useFormCsi).
 * See: .agents/skills/asan-confirmation-refactor/SKILL.md
 */
export const useFormCsiAction = (pathUrl?: string): FormCsiAction | null => {
  const navigate = useCustomNavigate();
  const location = useLocation();
  const sendToCsi = useSendSalaryToCsi();
  const formCsi = useFormCsi();

  const endpoint = pathUrl?.split('/')[4];

  if (!FORM_CSI_PATHS.includes(endpoint)) return null;

  return (btnType, response) => {
    sendToCsi.mutate(
      {
        req_model: { form_id: btnType },
        pathUrl: endpoint,
        business_key: response?.business_key,
      },
      {
        onSuccess: resp => {
          if (resp.form_id !== 'csi') {
            navigate(`${resp.form_id}`);
          } else {
            formCsi.open((rating: number, comment: string) => {
              sendToCsi.mutate(
                {
                  req_model: { rating, comment },
                  pathUrl: endpoint,
                  business_key: resp.business_key,
                },
                {
                  onSuccess: subResp => {
                    formCsi.close();
                    navigate(`${subResp.form_id}`);
                  },
                }
              );
            });
          }
        },
      }
    );

    const pathSegments = location.pathname.split('/');
    const page = pathSegments[pathSegments.length - 1];

    formCsi.setRedirectPage(btnType === 'go_home' ? 'home-page' : page);
  };
};
